import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  useTheme,
  useMediaQuery,
  TextField,
  Popper,
  Paper,
  ClickAwayListener,
  IconButton,
  Divider,
  Button,
  Typography,
  Tooltip,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDownRounded";
import FilterListRoundedIcon from "@mui/icons-material/FilterListRounded";
import { getGridButtonSelectorStyles } from "../theme";
import {
  POPPER_CONFIG,
  PAPER_CONFIG,
  DATE_RANGE_CONFIG,
  GRID_SIZES,
  SPACING,
} from "../constants/filterSelectors";

const STATUS_OPTIONS = ["Finalized", "Draft"];

const ExpensesFilterSelector = ({
  months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ],
  years = [],
  selectedMonths = [],
  selectedYears = [],
  selectedStatuses = STATUS_OPTIONS,
  onSave,
}) => {
  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");
  const triggerBoxShadow =
    "0 2px 8px rgba(0, 0, 0, 0.1), 0px 4px 8px rgba(0, 0, 0, 0.1)";
  const textFieldRef = useRef(null);
  const [anchorEl, setAnchorEl] = useState(null);

  const [localSelectedMonths, setLocalSelectedMonths] =
    useState(selectedMonths);
  const [localSelectedYears, setLocalSelectedYears] = useState(selectedYears);
  const [localSelectedStatuses, setLocalSelectedStatuses] = useState(
    selectedStatuses?.length ? selectedStatuses : STATUS_OPTIONS,
  );

  useEffect(() => {
    setLocalSelectedMonths(selectedMonths);
    setLocalSelectedYears(selectedYears);
    setLocalSelectedStatuses(
      selectedStatuses?.length ? selectedStatuses : STATUS_OPTIONS,
    );
  }, [selectedMonths, selectedYears, selectedStatuses]);

  const handleClick = () => {
    setAnchorEl(anchorEl ? null : textFieldRef.current);
  };

  const handleClose = () => {
    setLocalSelectedMonths(selectedMonths);
    setLocalSelectedYears(selectedYears);
    setLocalSelectedStatuses(
      selectedStatuses?.length ? selectedStatuses : STATUS_OPTIONS,
    );
    setAnchorEl(null);
  };

  const handleSave = () => {
    if (onSave) {
      onSave({
        months: localSelectedMonths,
        years: localSelectedYears,
        statuses: localSelectedStatuses,
      });
    }
    setAnchorEl(null);
  };

  const handleMonthToggle = (month) => {
    setLocalSelectedMonths((prev) => {
      if (prev.includes(month)) {
        return prev.filter((m) => m !== month);
      }
      return [...prev, month];
    });
  };

  const handleYearSelect = (year) => {
    setLocalSelectedYears([year]);
  };

  const handleStatusToggle = (status) => {
    setLocalSelectedStatuses((prev) => {
      const isSelected = prev.includes(status);

      if (isSelected) {
        if (prev.length === 1) {
          return prev;
        }
        return prev.filter((s) => s !== status);
      }

      return [...prev, status];
    });
  };

  const getDisplayText = () => {
    const monthPart =
      localSelectedMonths.length > 0
        ? `${localSelectedMonths.length} month${localSelectedMonths.length > 1 ? "s" : ""}`
        : "No months";

    const yearPart = localSelectedYears[0] || "No year";
    const statusPart =
      localSelectedStatuses.length === STATUS_OPTIONS.length
        ? "All statuses"
        : localSelectedStatuses.join(" + ");

    return `${monthPart} • ${yearPart} • ${statusPart}`;
  };

  const open = Boolean(anchorEl);
  const isApplyDisabled =
    localSelectedMonths.length === 0 || localSelectedStatuses.length === 0;

  return (
    <Box sx={{ position: "relative", width: "100%" }}>
      {isMobile ? (
        <Tooltip title="Expenses Filters">
          <IconButton
            onClick={handleClick}
            ref={textFieldRef}
            size="small"
            sx={{
              width: "40px",
              height: "40px",
              backgroundColor: theme.palette.background.default,
              boxShadow: triggerBoxShadow,
              borderRadius: "12px",
              border: "none",
              color: theme.palette.primary.main,
              "&:hover": {
                backgroundColor: theme.palette.primary.buttonHover,
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
              },
            }}
          >
            <FilterListRoundedIcon />
          </IconButton>
        </Tooltip>
      ) : (
        <TextField
          ref={textFieldRef}
          fullWidth
          size="small"
          label="Expenses Filters"
          value={getDisplayText()}
          onClick={handleClick}
          sx={{
            "& .MuiOutlinedInput-notchedOutline": {
              border: "none !important",
            },
            "& .MuiOutlinedInput-root": {
              borderRadius: "12px",
            },
            height: "100%",
            "& .MuiInputBase-root": {
              height: "100%",
              display: "flex",
              alignItems: "center",
              boxShadow: triggerBoxShadow,
              borderRadius: "12px",
              backgroundColor: theme.palette.background.default,
            },
            "& .MuiInputBase-input": {
              py: "8px !important",
              color: theme.palette.text.primary,
              fontSize: "0.875rem",
            },
          }}
          InputProps={{
            readOnly: true,
            endAdornment: (
              <IconButton
                edge="end"
                onClick={handleClick}
                sx={{ padding: 0.5 }}
              >
                <ArrowDropDownIcon />
              </IconButton>
            ),
          }}
        />
      )}

      <Popper
        open={open}
        anchorEl={anchorEl}
        placement="bottom-start"
        style={{ zIndex: POPPER_CONFIG.zIndex }}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <Paper
            elevation={PAPER_CONFIG.elevation}
            sx={{
              p: PAPER_CONFIG.padding,
              bgcolor: theme.palette.background.alt,
              border: `1px solid ${theme.palette.background.default}`,
              borderRadius: PAPER_CONFIG.borderRadius,
              maxHeight: "80vh",
              overflowY: "auto",
              width: isMobile ? "min(90vw, 360px)" : 560,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                gap: SPACING.containerGap,
              }}
            >
              <Box
                sx={{
                  width: isMobile ? "100%" : DATE_RANGE_CONFIG.width,
                  flexShrink: 0,
                }}
              >
                <Box sx={{ mb: SPACING.containerGap }}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      mb: 1,
                      fontWeight: "bold",
                      color: theme.palette.primary.text2,
                    }}
                  >
                    Months
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Button
                      onClick={() => {
                        setLocalSelectedMonths([...months]);
                      }}
                      size="small"
                      sx={{ color: theme.palette.primary.main }}
                    >
                      Select All
                    </Button>
                    <Button
                      onClick={() => {
                        setLocalSelectedMonths([]);
                      }}
                      size="small"
                      sx={{ color: theme.palette.primary.main }}
                    >
                      Select None
                    </Button>
                  </Box>
                  <Grid container spacing={SPACING.gridSpacing}>
                    {months.map((month) => {
                      const isSelected = localSelectedMonths.includes(month);
                      return (
                        <Grid size={GRID_SIZES.month} key={month}>
                          <Box
                            sx={getGridButtonSelectorStyles(theme, isSelected)}
                            onClick={() => handleMonthToggle(month)}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                color: isSelected
                                  ? "#ffffff"
                                  : theme.palette.primary.main,
                                fontWeight: isSelected ? "bold" : "normal",
                              }}
                            >
                              {month}
                            </Typography>
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>

                <Box>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      mb: 1,
                      fontWeight: "bold",
                      color: theme.palette.primary.text2,
                    }}
                  >
                    Years
                  </Typography>
                  <Grid container spacing={SPACING.gridSpacing}>
                    {years.map((year) => {
                      const isSelected = localSelectedYears.includes(year);
                      return (
                        <Grid size={GRID_SIZES.year} key={year}>
                          <Box
                            sx={getGridButtonSelectorStyles(theme, isSelected)}
                            onClick={() => handleYearSelect(year)}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                color: isSelected
                                  ? "#ffffff"
                                  : theme.palette.primary.main,
                                fontWeight: isSelected ? "bold" : "normal",
                              }}
                            >
                              {year}
                            </Typography>
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>
              </Box>

              {isMobile ? (
                <Divider sx={{ my: SPACING.containerGap }} />
              ) : (
                <Divider orientation="vertical" flexItem />
              )}

              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    mb: 1,
                    fontWeight: "bold",
                    color: theme.palette.primary.text2,
                  }}
                >
                  Status
                </Typography>

                <Grid container spacing={SPACING.gridSpacing}>
                  {STATUS_OPTIONS.map((status) => {
                    const isSelected = localSelectedStatuses.includes(status);

                    return (
                      <Grid size={12} key={status}>
                        <Box
                          sx={{
                            ...getGridButtonSelectorStyles(theme, isSelected),
                            py: SPACING.buttonPadding,
                            minHeight: "auto",
                          }}
                          onClick={() => handleStatusToggle(status)}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              color: isSelected
                                ? "#ffffff"
                                : theme.palette.primary.main,
                              fontWeight: isSelected ? "bold" : "normal",
                            }}
                          >
                            {status}
                          </Typography>
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: SPACING.gridSpacing,
              }}
            >
              <Button
                variant="outlined"
                onClick={handleClose}
                sx={{
                  borderRadius: PAPER_CONFIG.borderRadius,
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={isApplyDisabled}
                sx={{
                  bgcolor: theme.palette.primary.main,
                  color: theme.palette.background.default,
                  borderRadius: PAPER_CONFIG.borderRadius,
                  "&:hover": {
                    bgcolor: theme.palette.primary.buttonHoverSelected,
                  },
                  ...(isApplyDisabled && {
                    bgcolor: theme.palette.action.disabledBackground,
                    color: theme.palette.action.disabled,
                    cursor: "not-allowed",
                  }),
                }}
              >
                Apply
              </Button>
            </Box>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
};

export default ExpensesFilterSelector;